import React from 'react'
import { CodeEditor } from '@/features/code-editor'
import { useGetRoute } from '@/lib/react-query'
import Section from '@/components/ui/Section'
import { ReactComponent as EditorIcon } from '@/assets/icons/editor.svg'
import { ReactComponent as ResultsIcon } from '@/assets/icons/binary-code.svg'
import { ReactComponent as SaveIcon } from '@/assets/icons/save.svg'
import { ReactComponent as CodeIcon } from '@/assets/icons/code-fill.svg'
import { ReactComponent as BackIcon } from '@/assets/icons/chevron-left.svg'
import Typography from '@/components/ui/Typography'
import classes from './Route.module.scss'
import { Header } from '@/components/ui/Header'
import Button from '@/components/ui/Buttons/Button'
import {
  useConfirmUnsaved,
  useEditor,
  useEditorConfig,
  useEditorShortcuts,
  useGoBack,
  useModal,
  usePageParams,
  usePasteAtCursor,
  useRouteName,
  useSaveRoute,
} from '@/app/routes/Route/Route.hooks'
import { Puff } from '@/components/ui/Loaders'
import TitleInput from '@/components/ui/TitleInput'
import { RouteLogs, RunButton, PayloadSection, useCodeRunner, useCodeRunnerPayload } from '@/features/code-runner'
import { SecretsMenu, SecretModal } from '@/features/secrets'
import { ConnectorsMenu, ConnectorModal } from '@/features/connectors'
import { SnippetModal, SnippetsMenu } from '@/features/snippets'
import HistoryButton from '@/components/ui/Buttons/HistoryButton'
import HistoryModal from '@/components/HistoryModal'
import { ZendeskModal } from '@/features/zendesk-app'
import SettingsButton from '@/components/ui/Buttons/SettingsButton'
import RouteSettingsModal from '@/components/RouteSettingsModal'
import { RouteType } from '@/config/app'
import TrialSection from '@/components/TrialSection'
import { DeployButton } from '@/components/DeployButton'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { GridLayout } from '@/components/GridLayout'

export function RoutePage() {
  const { projectId, routeId } = usePageParams()
  const { data: getRoute } = useGetRoute(projectId, routeId)
  if (!getRoute) throw "Couldn't fetch route date"
  const { type } = getRoute.route
  const isCronRoute = type === RouteType.Cron
  const editorConfig = useEditorConfig(type, getRoute.config)
  const goBack = useGoBack()
  const [routeName, setRouteName] = useRouteName(getRoute.route)
  const modal = useModal()
  const editor = useEditor(getRoute.code ?? '')
  const [parentValue, paste] = usePasteAtCursor(editor.code, editor.cursorPosition)
  const resetConfirm = useConfirmUnsaved([routeName, editor.code])
  const [isSaveLoading, save] = useSaveRoute({
    projectId,
    id: routeId,
    code: editor.code,
    name: routeName,
    onSuccess: resetConfirm,
  })
  const [payload, setPayload] = useCodeRunnerPayload(getRoute.route?.type)
  const codeRunner = useCodeRunner({
    id: getRoute.route.id,
    code: editor.code,
    payload,
    runtime: getRoute.route.runtime,
  })
  useDocumentTitle(routeName)
  useEditorShortcuts(save)

  return (
    <div className={classes.container}>
      <Header>
        <div>
          <Button color={'neutral'} StartIcon={<BackIcon />} onClick={goBack} onAuxClick={goBack}>
            Back
          </Button>
          <TitleInput value={routeName} onSave={setRouteName} />
        </div>
        <div>
          <DeployButton projectId={projectId} routeId={routeId} />

          <Button variant={'soft'} StartIcon={isSaveLoading ? <Puff /> : <SaveIcon />} onClick={save}>
            {isSaveLoading ? 'Saving...' : 'Save'}
          </Button>
          <SettingsButton onClick={() => modal.open('route-settings')} />
        </div>
      </Header>
      <TrialSection className={classes.trialSection} />
      <div className={classes.body}>
        <GridLayout>
          <div key={'editor'}>
            <Section>
              <Section.Header title={'Editor'} icon={<EditorIcon />}>
                <ConnectorsMenu
                  onSelect={(connector) => modal.open('connector', { connector })}
                  onNewSelect={() => modal.open('new-connector')}
                />
                <SecretsMenu
                  projectId={projectId}
                  onSelect={(secret) => modal.open('secret', { secret })}
                  onNewSelect={() => modal.open('new-secret')}
                />
                <SnippetsMenu
                  onSelect={(snippet) => modal.open('snippet', { snippet })}
                  onNewSelect={() => modal.open('new-snippet')}
                />
                <RunButton onClick={() => codeRunner.run()} isLoading={codeRunner.isPending} />
              </Section.Header>
              <CodeEditor
                config={editorConfig!}
                initialValue={editor.code}
                parentValue={parentValue}
                onModelChange={editor.onModelChange}
                onCursorChanged={editor.onCursorChanged}
                onScrollChange={editor.onScrollChange}
                onSelectionChanged={editor.onSelectionChanged}
              />
            </Section>
          </div>

          <div key={'payload'}>
            <PayloadSection
              className={classes.payloadSection}
              isEnabled={!isCronRoute}
              value={payload}
              setValue={setPayload}
            />
          </div>

          <div key={'results'}>
            <Section>
              <Section.Header title={'Results'} icon={<ResultsIcon />} isActive={codeRunner.isSuccess}>
                <HistoryButton onClick={() => modal.open('history')} />
              </Section.Header>

              <Section.Content isLoading={codeRunner.isPending} isCentered={!codeRunner.isSuccess}>
                {codeRunner.isSuccess ? (
                  <RouteLogs logs={codeRunner.result.messages} />
                ) : codeRunner.isError ? (
                  <>
                    <div className={classes.overlayIcon}>
                      <CodeIcon />
                    </div>
                    <Typography>Error</Typography>
                  </>
                ) : (
                  <>
                    <div className={classes.overlayIcon}>
                      <CodeIcon />
                    </div>
                    <Typography variant={'xl'}>Waiting for code</Typography>
                  </>
                )}
              </Section.Content>

              <Section.Footer>
                <div className={classes.row}>
                  <Typography variant={'caption'}>Response time</Typography>
                  <Typography>{`${codeRunner.result.executionTime} ms`}</Typography>
                </div>
              </Section.Footer>
            </Section>
          </div>
        </GridLayout>
      </div>
      {modal.name === 'snippet' && (
        <SnippetModal {...modal.props} onClose={modal.close} config={editorConfig!} onPaste={paste} />
      )}
      {modal.name === 'new-snippet' && <SnippetModal {...modal.props} onClose={modal.close} config={editorConfig!} />}
      {modal.name === 'secret' && <SecretModal {...modal.props} onClose={modal.close} projectId={projectId} />}
      {modal.name === 'new-secret' && <SecretModal {...modal.props} onClose={modal.close} projectId={projectId} />}
      {modal.name === 'connector' && <ConnectorModal {...modal.props} onClose={modal.close} projectId={projectId} />}
      {modal.name === 'new-connector' && (
        <ConnectorModal {...modal.props} onClose={modal.close} projectId={projectId} />
      )}
      {modal.name === 'history' && (
        <HistoryModal {...modal.props} onClose={modal.close} projectId={projectId} routeId={routeId} />
      )}
      {modal.name === 'zendesk' && (
        <ZendeskModal {...modal.props} onClose={modal.close} projectId={projectId} routeId={routeId} />
      )}
      {modal.name === 'route-settings' && (
        <RouteSettingsModal {...modal.props} onClose={modal.close} projectId={projectId} routeId={routeId} />
      )}
    </div>
  )
}
